import React, { forwardRef, useState } from 'react';
import Modal, {
  ModalProps,
  ModalRef,
} from '@/components/@IntranetLibrary/Modal';
import { Button, Stack } from '@mui/material';
import { ButtonWrapper } from '@/components/@IntranetLibrary/Button/ButtonWrapper';
import FormProvider from '@/components/HookForm/FormProvider';
import usePasswordForm, {
  _passwordForm,
  PasswordForm,
} from '@/hooks/form/usePasswordForm';
import { FormGroup } from '@/components/Board/CreateBoardModal/BoardForm/BoardFormField/style';
import Label from '@/components/@IntranetLibrary/Label';
import RHFPasswordField from '@/components/HookForm/RHFPasswordField';
import {
  useChangePassWord,
  useUpdatePasswordFreshFarm,
} from '@/hooks/apiHooks';
import { AxiosError } from 'axios';
import { useSnackbar } from '@/hooks/global-tools';
import { useTranslation } from 'react-i18next';

export const AccountType = {
  FNF: 'F&F 계정',
  FRESH_FARM: '올리브 식권 계정',
} as const;
export type AccountTypeValue = (typeof AccountType)[keyof typeof AccountType];

type PasswordSettingModalProps = {
  accountType: AccountTypeValue;
  passportId: string;
} & Pick<ModalProps, 'handleClose'>;

const ChangePasswordModal = forwardRef<ModalRef, PasswordSettingModalProps>(
  (props, ref) => {
    const { t } = useTranslation();
    const { accountType, passportId, handleClose } = props;
    const [isLoading, setIsLoading] = useState(false);

    const { showSuccess, showError } = useSnackbar();

    const methods = usePasswordForm();
    const {
      handleSubmit,
      reset,
      formState: { isValid, isSubmitting },
    } = methods;

    const handleConfirm = () => {
      if (isLoading) return;
      setIsLoading(true);

      handleSubmit(async (data: PasswordForm) => {
        try {
          if (accountType === AccountType.FNF) {
            await useChangePassWord(data);
          } else if (accountType === AccountType.FRESH_FARM) {
            await useUpdatePasswordFreshFarm(passportId, data);
          }

          showSuccess({ message: t('MYPAGE.비밀번호가 변경되었습니다.') });
          close();
        } catch (e: any) {
          let message = t('MYPAGE.비밀번호 변경에 실패했습니다.');

          if (e instanceof AxiosError) {
            const errorData = e.response?.data;
            if (errorData?.message) {
              message = errorData?.message;
            }
          }

          showError({
            message,
          });
        }
        setIsLoading(false);
      })();
    };

    const close = () => {
      handleClose?.();
      reset(_passwordForm);
    };

    return (
      <Modal
        ref={ref}
        title={`${t(`MYPAGE.${accountType}`)} ${t('MYPAGE.비밀번호 변경')}`}
        size={'x-small'}
        handleClose={close}
      >
        <Stack gap={'24px'}>
          <FormProvider methods={methods}>
            <Stack gap={'24px'}>
              <FormGroup gapsize={'s'}>
                <Label
                  isRequired={true}
                  title={t('MYPAGE.현재 비밀번호')}
                  size={14}
                />
                <RHFPasswordField
                  name={'currentPassword'}
                  placeholder={t(
                    'MYPAGE.현재 사용중인 비밀번호를 입력해 주세요',
                  )}
                  disabled={isSubmitting}
                  slotProps={{ htmlInput: { maxLength: 16 } }}
                />
              </FormGroup>
              <FormGroup gapsize={'s'}>
                <Label
                  isRequired={true}
                  title={t('MYPAGE.새 비밀번호')}
                  size={14}
                />
                <RHFPasswordField
                  name={'updatePassword'}
                  placeholder={t(
                    'MYPAGE.새롭게 사용할 비밀번호를 입력해 주세요',
                  )}
                  disabled={isSubmitting}
                  slotProps={{ htmlInput: { maxLength: 16 } }}
                />
                <RHFPasswordField
                  name={'updateConfirmPassword'}
                  placeholder={t(
                    'MYPAGE.새롭게 사용할 비밀번호를 한 번 더 입력해 주세요',
                  )}
                  helperText={
                    accountType === AccountType.FRESH_FARM
                      ? t('MYPAGE.변경한 비밀번호는 11시, 17시에 적용됩니다.')
                      : undefined
                  }
                  disabled={isSubmitting}
                  slotProps={{ htmlInput: { maxLength: 16 } }}
                  onKeyDown={(event) => {
                    if (isValid && event.key === 'Enter') handleConfirm();
                  }}
                />
              </FormGroup>
            </Stack>
          </FormProvider>

          <ButtonWrapper>
            <Button variant={'outlined'} size={'large'} onClick={close}>
              {t('취소')}
            </Button>
            <Button
              variant={'contained'}
              size={'large'}
              disabled={!isValid || isSubmitting}
              loading={isLoading}
              onClick={handleConfirm}
            >
              {t('MYPAGE.비밀번호 변경')}
            </Button>
          </ButtonWrapper>
        </Stack>
      </Modal>
    );
  },
);
ChangePasswordModal.displayName = 'ChangePasswordModal';

export default ChangePasswordModal;
